import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import type { SearchResultItem } from "@onemileauto/shared-types";
import { useSearch } from "../../hooks/use-search";
import { formatRelativeTime } from "../../lib/format";
import { EmptyState } from "./empty-state";

const SOH = String.fromCharCode(1);

/** Renders an FTS5 snippet with SOH-delimited match segments bolded — see SearchResultItem in shared-types for why this is plain text, never HTML. */
function HighlightedSnippet({ snippet }: { snippet: string }) {
  const parts = snippet.split(SOH);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="rounded-sm bg-rust-soft px-0.5 text-rust-deep">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function SearchResultRow({ result, selectedThreadId }: { result: SearchResultItem; selectedThreadId?: string }) {
  const selected = result.threadId === selectedThreadId;
  return (
    <Link
      to="/$threadId"
      params={{ threadId: result.threadId }}
      search={(prev) => prev}
      className={`block border-b border-border px-4 py-3 transition-colors ${
        selected ? "bg-rust-soft/40" : "hover:bg-parchment-dim"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-medium text-charcoal">{result.subject || "(no subject)"}</p>
        <span className="shrink-0 text-xs text-muted">{formatRelativeTime(result.lastMessageAt)}</span>
      </div>
      <p className="mt-0.5 truncate text-xs text-muted">{result.senderEmail}</p>
      <p className="mt-1 line-clamp-2 text-sm text-charcoal/80">
        <HighlightedSnippet snippet={result.snippet} />
      </p>
    </Link>
  );
}

interface SearchResultsProps {
  mailboxId: string | undefined;
  query: string;
  selectedThreadId?: string;
}

export function SearchResults({ mailboxId, query, selectedThreadId }: SearchResultsProps) {
  const { data, isLoading } = useSearch(mailboxId, query);

  if (isLoading) {
    return <div className="px-4 py-6 text-center text-sm text-muted">Searching&hellip;</div>;
  }

  const hasKeywordResults = Boolean(data && data.results.length > 0);
  const hasSimilarResults = Boolean(data && data.similar.length > 0);

  if (!data || (!hasKeywordResults && !hasSimilarResults)) {
    return (
      <EmptyState
        icon={Search}
        title="No results"
        description={`Nothing matched "${query}". Try a different word.`}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      {hasKeywordResults && (
        <>
          <p className="px-4 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-muted">
            {data.results.length} result{data.results.length === 1 ? "" : "s"}
          </p>
          {data.results.map((r) => (
            <SearchResultRow key={r.messageId} result={r} selectedThreadId={selectedThreadId} />
          ))}
        </>
      )}

      {/* "Similar messages" (Phase 7) — semantically related mail the exact-
          word search above wouldn't catch, framed in plain language and
          visually separated as its own section so it never reads as more
          keyword matches. Deliberately the same SearchResultRow used above
          (no score, no technical labeling anywhere in this data — see
          SearchResponse's docstring in shared-types) so it feels like more
          of the same product, not a bolted-on technical feature. */}
      {hasSimilarResults && (
        <>
          <p
            className={`px-4 pb-1 text-xs font-medium uppercase tracking-wide text-muted ${hasKeywordResults ? "mt-3 border-t border-border pt-3" : "pt-3"}`}
          >
            Similar messages
          </p>
          {data.similar.map((r) => (
            <SearchResultRow key={r.messageId} result={r} selectedThreadId={selectedThreadId} />
          ))}
        </>
      )}
    </div>
  );
}
